import { supabase } from '../supabase';
import { MarketData } from '../utils/types';
import { subMonths, subDays } from 'date-fns';

const validColumns = [
  'date',
  'report',
  'season',
  'week',
  'region',
  'item',
  'transport',
  'origin',
  'exporter',
  'brand',
  'variety',
  'volume',
  'sold_volume',
  'package',
  'class',
  'seller',
  'owner',
  'quality',
  'new',
  'price',
  'size',
  'color',
  'kg',
  'weight_kg',
  'price_kg',
  'grower_no',
  'species_id',
];

const requiredColumns = ['size', 'color', 'price_kg'];

export async function uploadMarketDataToDb(
  data: Partial<Omit<MarketData, 'id'>>[],
  months: number = 4,
  speciesIds?: number[],
  mode: 'replace' | 'append' = 'replace'
) {
  try {
    // Calculate date limit (X months ago, previous day at noon)
    const now = new Date();
    const monthsAgo = subMonths(now, months);
    const limitDate = subDays(monthsAgo, 1);
    limitDate.setHours(12, 0, 0, 0);

    console.log('Fecha actual:', now.toISOString());
    console.log('Fecha límite:', limitDate.toISOString());
    console.log('IDs de especies a actualizar:', speciesIds || 'Todas');

    console.log('total before filter', data.length);
    const filteredData = data.filter((record) => {
      const recordDate = new Date(record.date as string);
      return recordDate >= limitDate;
    });
    console.log('total after filter', filteredData.length);
    // Las filas fuera de la ventana se descartan silenciosamente: se cuentan
    // para poder avisar (un archivo de temporada vieja se perdía entero).
    const discardedByDate = data.length - filteredData.length;

    console.log(`Total records before cleaning: ${filteredData.length}`);

    // Clean the data
    const cleanData = filteredData
      .map((record, index) => {
        const cleanRecord: Record<string, unknown> = {};

        // Primero verificar que todos los campos requeridos existan
        const missingRequiredFields = requiredColumns.filter(
          (col) =>
            !record[col as keyof typeof record] ||
            record[col as keyof typeof record] === '' ||
            record[col as keyof typeof record] === '""'
        );

        if (missingRequiredFields.length > 0) {
          console.warn(
            `Record ${index + 1} skipped:`,
            `\nDate: ${record.date}`,
            `\nVariety: ${record.variety}`,
            `\nExporter: ${record.exporter}`,
            `\nMissing fields: ${missingRequiredFields.join(', ')}`,
            `\nOriginal record:`,
            record
          );
          return null;
        }

        // Si todos los campos requeridos existen, proceder con la limpieza normal
        for (const [key, value] of Object.entries(record)) {
          if (
            validColumns.includes(key) &&
            value !== undefined &&
            value !== null &&
            value !== '' &&
            value !== '""' &&
            !key.includes('__empty')
          ) {
            cleanRecord[key] = value;
          }
        }

        // Validación adicional para campos específicos
        if (!cleanRecord.size || cleanRecord.size === '') {
          console.warn(
            `Record ${index + 1} has invalid size:`,
            `\nDate: ${record.date}`,
            `\nVariety: ${record.variety}`,
            `\nExporter: ${record.exporter}`,
            `\nSize value: "${cleanRecord.size}"`
          );
          return null;
        }

        return cleanRecord;
      })
      .filter((record): record is Record<string, unknown> => record !== null);

    console.log(`Valid records after cleaning: ${cleanData.length}`);

    if (cleanData.length === 0) {
      throw new Error(
        discardedByDate === data.length
          ? `Todas las filas quedaron fuera del rango de ${months} meses (anteriores al ${limitDate
              .toISOString()
              .slice(0, 10)}). Aumentá el rango de meses para incluirlas.`
          : 'No valid records found after cleaning'
      );
    }

    // Log sample of first few records for debugging
    console.log(
      'Sample of first 3 records:',
      cleanData.slice(0, 3).map((record) => ({
        date: record.date,
        variety: record.variety,
        size: record.size,
        color: record.color,
        price_kg: record.price_kg,
        species_id: record.species_id,
      }))
    );

    console.log('speciesIds (IDs numéricos):', speciesIds);

    console.log('calling the upload function rpc');
    const { data: result, error } = await supabase.rpc('upload_market_data', {
      clean_data: cleanData,
      months_param: months,
      species_ids: speciesIds,
      upload_mode: mode,
    });

    console.log('result', result);
    console.log('error', error);

    if (error) throw new Error(error.message);

    return {
      success: true,
      rowsReceived: data.length,
      rowsUploaded: cleanData.length,
      discardedByDate,
      cutoffDate: limitDate.toISOString().slice(0, 10),
      months,
      // El RPC ahora reemplaza exactamente el rango de fechas del archivo y
      // devuelve el detalle: {deleted_rows, inserted_rows, date_from, date_to}.
      replaced: result ?? null,
    };
  } catch (error) {
    console.error('Error in uploadMarketDataToDb:', error);
    // El mensaje del RPC importa: en modo agregar explica por qué se bloqueó
    // ("ya existen N filas entre X e Y...").
    return {
      success: false,
      rowsReceived: data.length,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/* export async function uploadMarketDataToDb(
  data: Partial<Omit<MarketData, 'id'>>[]
) {
  try {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/upload-market-data`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_KEY}`,
        },
        body: JSON.stringify({ data }),
      }
    );

    const result = await response.json();
    if (!result.success) throw new Error(result.error);

    return true;
  } catch (error) {
    console.error('Error in uploadMarketDataToDb:', error);
    return false;
  }
} */

export async function getMarketDataBySelectedDate(
  selectedDate: string,
  speciesId?: number
): Promise<MarketData[]> {
  selectedDate = selectedDate.split('T')[0];
  try {
    let query = supabase
      .from('market_data')
      .select('*')
      .eq('date', selectedDate);

    // Aplicar filtro de especie si se proporciona
    if (speciesId) {
      query = query.eq('species_id', speciesId);
    }

    const { data, error } = await query;

    if (error) {
      throw new Error(`Error fetching market data: ${error.message}`);
    }

    return data || [];
  } catch (error) {
    console.error('Error reading market data:', error);
    return [];
  }
}

export async function getMarketDataByWeek(
  weekNumber: number,
  season?: string
): Promise<MarketData[]> {
  try {
    let query = supabase.from('market_data').select('*').eq('week', weekNumber);

    if (season) {
      query = query.eq('season', season);
    }

    const { data, error } = await query;

    if (error) {
      throw new Error(`Error fetching market data: ${error.message}`);
    }

    return data || [];
  } catch (error) {
    console.error('Error reading market data:', error);
    return [];
  }
}

export async function getAllMarketData(
  seasons?: string[],
  speciesId?: number
): Promise<MarketData[]> {
  try {
    // First get total count
    let countQuery = supabase
      .from('market_data')
      .select('*', { count: 'exact', head: true });

    if (seasons && seasons.length > 0) {
      countQuery = countQuery.in('season', seasons);
    }

    // Apply species filter if provided
    if (speciesId) {
      countQuery = countQuery.eq('species_id', speciesId);
    }

    const { count, error: countError } = await countQuery;
    console.log('count', count);

    if (countError) {
      throw new Error(`Error getting count: ${countError.message}`);
    }

    if (!count) return [];

    const pageSize = 9999;
    const totalPages = Math.ceil(count / pageSize);

    // Create array of promises for each page
    const promises = Array.from({ length: totalPages }, (_, page) => {
      const start = page * pageSize;
      const end = start + pageSize - 1;

      let query = supabase.from('market_data').select('*').range(start, end);

      if (seasons && seasons.length > 0) {
        query = query.in('season', seasons);
      }

      // Apply species filter if provided
      if (speciesId) {
        query = query.eq('species_id', speciesId);
      }

      return query;
    });

    // Execute all promises in parallel
    const results = await Promise.all(promises);
    console.log('results', results);
    // Combine and check for errors
    const allData = results.reduce((acc, { data, error }) => {
      if (error) {
        throw new Error(`Error fetching market data: ${error.message}`);
      }
      return data ? [...acc, ...data] : acc;
    }, [] as MarketData[]);

    return allData;
  } catch (error) {
    console.error('Error reading market data:', error);
    return [];
  }
}

export async function getMarketDataByDateRange(
  startDate: string,
  endDate: string,
  speciesId?: number
): Promise<MarketData[]> {
  // Convert dates to YYYY-MM-DD format
  startDate = startDate.split('T')[0];
  endDate = endDate.split('T')[0];

  try {
    // First get total count

    let countQuery = supabase
      .from('market_data')
      .select('*', { count: 'exact', head: true })
      .gte('date', startDate)
      .lte('date', endDate);

    // Apply species filter if provided
    if (speciesId) {
      countQuery = countQuery.eq('species_id', speciesId);
    }

    const { count, error: countError } = await countQuery;

    if (countError) {
      throw new Error(`Error getting count: ${countError.message}`);
    }

    if (!count) return [];

    const pageSize = 1000;
    const totalPages = Math.ceil(count / pageSize);

    // Create array of promises for each page
    const promises = Array.from({ length: totalPages }, (_, page) => {
      const start = page * pageSize;
      const end = start + pageSize - 1;

      let dataQuery = supabase
        .from('market_data')
        .select('*')
        .gte('date', startDate)
        .lte('date', endDate)
        .order('date', { ascending: true }) // Ensure consistent ordering
        .order('id', { ascending: true }) // Secondary sort by id for stability
        .range(start, end);

      // Apply species filter if provided
      if (speciesId) {
        dataQuery = dataQuery.eq('species_id', speciesId);
      }

      return dataQuery;
    });

    // Execute all promises in parallel
    const results = await Promise.all(promises);

    // Combine and check for errors
    const allData = results.reduce((acc, { data, error }) => {
      if (error) {
        throw new Error(`Error fetching market data: ${error.message}`);
      }
      return data ? [...acc, ...data] : acc;
    }, [] as MarketData[]);

    return allData;
  } catch (error) {
    console.error('Error reading market data:', error);
    return [];
  }
}

export async function getLastAvailableMarketDate(
  speciesId?: number,
  season?: string,
  origins?: string[]
): Promise<string | null> {
  try {
    console.log('speciesId', speciesId);
    console.log('season', season);

    let query = supabase
      .from('market_data')
      .select('date')
      .order('date', { ascending: false })
      .limit(1);

    // Aplicar filtro de especie si se proporciona
    if (speciesId) {
      query = query.eq('species_id', speciesId);
    }

    // Aplicar filtro de temporada si se proporciona
    if (season) {
      query = query.eq('season', season);
    }

    // Aplicar filtro de origen. Sin esto el rango de fechas se ancla en la
    // última fecha del dataset completo, que puede ser fruta del hemisferio
    // norte (julio) mientras la chilena termina en febrero — el gráfico queda
    // vacío al filtrar por Chile.
    if (origins && origins.length > 0) {
      query = query.in('origin', origins);
    }

    const { data, error } = await query;

    if (error) {
      throw new Error(`Error fetching last market date: ${error.message}`);
    }

    if (data && data.length > 0) {
      // Convert the date to YYYY-MM-DD forma
      return data[0].date.split('T')[0];
    }

    return null;
  } catch (error) {
    console.error('Error getting last available market date:', error);
    return null;
  }
}

export interface DashboardMarketTodayResult {
  last_available_date: string | null;
  start_date: string | null;
  end_date: string | null;
  rows: MarketData[];
}

export async function getDashboardMarketToday(
  speciesId: number,
  season: string,
  daysBack: number = 7
): Promise<DashboardMarketTodayResult | null> {
  try {
    const { data, error } = await supabase.rpc('get_dashboard_market_today', {
      p_species_id: speciesId,
      p_season: season,
      p_days_back: daysBack,
    });

    if (error) throw error;

    const result = (data || {}) as Partial<DashboardMarketTodayResult>;
    return {
      last_available_date: result.last_available_date || null,
      start_date: result.start_date || null,
      end_date: result.end_date || null,
      rows: Array.isArray(result.rows) ? result.rows : [],
    };
  } catch (error) {
    console.error('Error getting dashboard market today:', error);
    return null;
  }
}

export interface DashboardSeasonComparisonRow {
  season: string;
  variety: string;
  week: number;
  median_price_kg: number;
  sample_count: number;
}

export interface DashboardSeasonComparisonResult {
  last_available_date: string | null;
  current_week: number;
  rows: DashboardSeasonComparisonRow[];
  top_varieties: string[];
  // Orígenes con precios en las dos temporadas, para el selector.
  origins: string[];
}

export async function getDashboardSeasonComparison(
  speciesId: number,
  currentSeason: string,
  previousSeason: string,
  // null = todos los orígenes
  origin: string | null = null
): Promise<DashboardSeasonComparisonResult | null> {
  try {
    const { data, error } = await supabase.rpc(
      'get_dashboard_season_comparison',
      {
        p_species_id: speciesId,
        p_current_season: currentSeason,
        p_previous_season: previousSeason,
        p_origin: origin,
      }
    );

    if (error) throw error;

    const result = (data || {}) as Partial<DashboardSeasonComparisonResult>;
    const rows = Array.isArray(result.rows) ? result.rows : [];

    return {
      last_available_date: result.last_available_date || null,
      current_week: Number(result.current_week) || 0,
      rows: rows.map((row) => ({
        ...row,
        week: Number(row.week) || 0,
        median_price_kg: Number(row.median_price_kg) || 0,
        sample_count: Number(row.sample_count) || 0,
      })),
      top_varieties: Array.isArray(result.top_varieties)
        ? result.top_varieties
        : [],
      origins: Array.isArray(result.origins) ? result.origins : [],
    };
  } catch (error) {
    console.error('Error getting dashboard season comparison:', error);
    return null;
  }
}

export interface ExporterWithBrands {
  exporter: string;
  brands: string[];
  /** Orígenes de la fruta de esa exportadora (CHILE, USA…), el principal primero. */
  origins: string[];
}

type ExporterBrandsResponse = {
  brands?: string[];
};

export async function getBrandsForExporter(
  exporter: string,
  speciesId?: number,
  season?: string
): Promise<string[]> {
  const exporterName = exporter.trim();
  if (!exporterName) return [];

  try {
    const { data, error } = await supabase.rpc('get_exporter_brands', {
      p_exporter: exporterName,
      p_species_id: speciesId ?? null,
      p_season: season ?? null,
    });

    if (error) throw error;

    return (
      ((data as ExporterBrandsResponse | null)?.brands || []) as string[]
    ).sort((a, b) => a.localeCompare(b));
  } catch (error) {
    console.warn(
      'Could not fetch exporter brands via RPC, falling back:',
      error
    );
  }

  const brands = new Set<string>();
  const pageSize = 1000;
  let from = 0;

  let hasMore = true;

  while (hasMore) {
    let query = supabase
      .from('market_data')
      .select('brand')
      .eq('exporter', exporterName)
      .not('brand', 'is', null)
      .not('brand', 'eq', '')
      .order('brand', { ascending: true })
      .range(from, from + pageSize - 1);

    if (speciesId) {
      query = query.eq('species_id', speciesId);
    }

    if (season) {
      query = query.eq('season', season);
    }

    const { data, error } = await query;

    if (error) {
      throw new Error(`Error fetching exporter brands: ${error.message}`);
    }

    (data || []).forEach((item) => {
      const brand = item.brand?.trim();
      if (brand) brands.add(brand);
    });

    hasMore = Boolean(data && data.length === pageSize);
    from += pageSize;
  }

  return Array.from(brands).sort((a, b) => a.localeCompare(b));
}

/**
 * Get unique exporters and their brands from market data
 * @param speciesId - Filter by species ID
 * @param season - Filter by season
 * @returns Array of exporters with their associated brands
 */
export async function getExportersWithBrands(
  speciesId?: number,
  season?: string
): Promise<ExporterWithBrands[]> {
  try {
    let query = supabase
      .from('market_data')
      .select('exporter, brand, origin')
      .not('exporter', 'is', null)
      .not('exporter', 'eq', '');

    // Apply species filter if provided
    if (speciesId) {
      query = query.eq('species_id', speciesId);
    }

    // Apply season filter if provided
    if (season) {
      query = query.eq('season', season);
    }

    const { data, error } = await query;

    if (error) {
      throw new Error(`Error fetching exporters: ${error.message}`);
    }

    if (!data) return [];

    // Group brands by exporter
    const exporterMap = new Map<string, Set<string>>();
    const originMap = new Map<string, Map<string, number>>();

    data.forEach((item) => {
      const exporter = item.exporter;
      const brand = item.brand;

      if (!exporterMap.has(exporter)) {
        exporterMap.set(exporter, new Set());
        originMap.set(exporter, new Map());
      }
      if (item.origin) {
        const counts = originMap.get(exporter)!;
        counts.set(item.origin, (counts.get(item.origin) || 0) + 1);
      }

      // Only add brand if it's not null or empty
      if (brand && brand.trim() !== '') {
        exporterMap.get(exporter)!.add(brand);
      }
    });

    // Convert to array format and sort
    const result: ExporterWithBrands[] = Array.from(exporterMap.entries())
      .map(([exporter, brandsSet]) => ({
        exporter,
        brands: Array.from(brandsSet).sort(),
        origins: Array.from(originMap.get(exporter) ?? new Map<string, number>())
          .sort((a, b) => b[1] - a[1])
          .map(([origin]) => origin),
      }))
      .sort((a, b) => a.exporter.localeCompare(b.exporter));

    return result;
  } catch (error) {
    console.error('Error getting exporters with brands:', error);
    return [];
  }
}
